/**
 * The body of the coach drawer (screens §P10): the suggested concept card, the caller's thread (round briefs and replies land here), three starter
 * questions on a new thread, the hint buttons, and the composer. A reply streams in sentence by sentence; every number in it was checked server-side.
 */
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRoute, useRoutes } from "../api/context";
import { whyExplanations } from "../cockpit/Why";
import { fiscalYearLabel, yearNamer } from "../format";
import { useLocation } from "../shell/history";
import { Icon } from "../theme/icons";
import { GxBadge } from "../theme/primitives";
import { GxLimitedText, INPUT_LIMITS } from "../theme/GxLimitedText";
import type { CoachContextRef } from "./CoachDrawer";
import { HINT_ASKS, starterQuestions } from "./starters";
import { priorPriceOf, resultsAnswer } from "../results/priceLine";
import { offlineAnswer } from "./offline";
import { cardAnswerReply } from "./cards";
import { ERROR_COPY, useCoachTurn } from "./useCoachTurn";
import "./coach.css";
import { RovingGroup } from "../theme/roving";

/** The cohort the drawer is open in: `/c/:cohortId/…` (a facilitated round) or `/solo/:runId/…` (a run is a cohort). */
const cohortOf = (pathname: string): string | null => /^\/(?:c|solo)\/([^/]+)/.exec(pathname)?.[1] ?? null;

/** The one line the thread opens on. */
export const WELCOME = "Ask about a term, a decision or last year's result. I explain; I do not choose for you.";

const HINT_MARKS = ["①", "②", "③"] as const;

/** A question answered on the spot from the glossary or the attribution; it lives with the open drawer, not in the stored thread. */
interface LocalTurn { readonly id: string; readonly at: number; readonly question: string; readonly answer: string }

const Bubble = ({ role, text, error, testId }: { readonly role: "me" | "coach"; readonly text: string; readonly error?: boolean; readonly testId?: string }) => (
  role === "me" ? <p className="gx-coach-msg gx-coach-msg--me">{text}</p>
    : <div className={`gx-coach-msg gx-coach-msg--coach${error === true ? " gx-coach-msg--error" : ""}`} data-testid={testId}><span className="gx-coach-by"><Icon name="spark" size={12} />Coach</span>{text.split(/\n{2,}/).map((p) => <p key={p}>{p}</p>)}</div>
);

export function CoachThread({ context }: { readonly context: CoachContextRef }) {
  const { pathname } = useLocation();
  const cohortId = cohortOf(pathname);
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const teamId = me.data?.principal.memberships.find((m) => m.cohortId === cohortId)?.teamId ?? null;
  // Who can read the thread depends on the game: a solo player's notes are theirs alone.
  const solo = me.data?.principal.memberships.some((m) => m.cohortId === cohortId && m.cohortMode === "solo") === true;
  const catalog = useRoute("contentCatalog", cohortId === null ? null : { cohortId }, { staleTime: 60_000 });
  const history = useRoute("coachHistory", teamId === null ? null : { teamId });
  // What the latest released year says about each measure answers "why did it move" from any screen, not only where the context carried it.
  const team = useRoute("teamState", teamId === null ? null : { teamId }, { staleTime: 30_000 });
  const latest = team.data?.releasedRounds.at(-1);
  const report = useRoute("roundReport", teamId === null || latest === undefined ? null : { teamId, roundId: latest.roundId }, { staleTime: Infinity });
  const { pending, busy, ask } = useCoachTurn(teamId, context);
  const [draft, setDraft] = useState("");
  const [over, setOver] = useState(false); // past the box's limit: the question is kept, not sent (OR-30.81)
  const [open, setOpen] = useState<"explainer" | "mistakes" | null>(null);
  const end = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const card = catalog.data === undefined ? undefined
    : catalog.data.conceptCards.find((c) => c.id === context.conceptId)
      ?? catalog.data.conceptCards.find((c) => c.id === catalog.data.decisions.find((d) => d.decisionId === context.decisionId)?.coachCardIds[0]);
  const messages = history.data?.messages ?? [];
  const [local, setLocal] = useState<readonly LocalTurn[]>([]);
  const glossary = catalog.data?.glossary;
  const explain = context.explain ?? (report.data === undefined || catalog.data === undefined ? {} : whyExplanations(report.data.attribution, catalog.data, fiscalYearLabel(catalog.data, report.data.roundNo)));
  const firstYear = team.data !== undefined && latest === undefined;
  const before = team.data?.releasedRounds.find((r) => r.roundNo === (latest?.roundNo ?? 0) - 1);
  // Every released year's report, for "what did my swap do": a question about a card answered in an earlier round is answered from that round's report.
  const released = team.data?.releasedRounds ?? [];
  const allReports = useRoutes("roundReport", teamId === null ? [] : released.map((r) => ({ teamId, roundId: r.roundId })));
  const previous = useRoute("roundReport", teamId === null || before === undefined ? null : { teamId, roundId: before.roundId }, { staleTime: Infinity });
  const prior = report.data === undefined ? null : priorPriceOf(previous.data ?? null, team.data?.kpiHistory.find((h) => h.roundNo === report.data.roundNo - 1)?.kpis.price);
  const results = context.results ?? (report.data === undefined ? null : resultsAnswer(report.data, prior, glossary));

  const answerLocally = (question: string, answer: string | null) => setLocal((l) => [...l, { id: `local-${l.length}`, at: Date.now(), question, answer: answer ?? "" }]);
  /** The glossary or the attribution first (deterministic, no model); the provider only for what they cannot answer. */
  const route = (text: string) => {
    const answer = cardAnswerReply(text, [...allReports.map((q) => q.data)].reverse(), yearNamer(catalog.data)) ?? (glossary === undefined ? null : offlineAnswer(text, glossary, explain, firstYear, results, catalog.data?.decisions));
    if (answer === null) ask(text); else answerLocally(text, answer);
  };

  // A context that arrives with a question ("Explain this number") asks it once, when the thread is ready: its own answer, else the same routing.
  const asked = useRef(false);
  useEffect(() => {
    if (context.ask === undefined || asked.current) return;
    if (context.answer !== undefined) { asked.current = true; answerLocally(context.ask, context.answer); }
    else if (teamId !== null && history.data !== undefined && glossary !== undefined) { asked.current = true; route(context.ask); }
  }, [context.ask, context.answer, glossary, history.data, teamId]);
  useEffect(() => { end.current?.scrollIntoView?.({ block: "end" }); }, [messages.length, local.length, pending?.userText, pending?.reply]);
  useEffect(() => () => { void queryClient.invalidateQueries({ queryKey: ["coachHistory"] }); }, [queryClient]);

  // The stored thread and the on-the-spot answers, in the order they were asked.
  const turns = [
    ...messages.map((m) => ({ id: m.id, at: Date.parse(m.createdAt), role: m.role === "user" ? "me" as const : "coach" as const, text: m.text })),
    ...local.flatMap((t) => [{ id: `${t.id}-q`, at: t.at, role: "me" as const, text: t.question }, { id: `${t.id}-a`, at: t.at + 1, role: "coach" as const, text: t.answer }]),
  ].sort((a, b) => a.at - b.at);
  // The Coach's own recaps (a coach message before the player has asked anything) are not posted by default: the thread opens on one welcome line.
  const firstAsk = turns.findIndex((t) => t.role === "me");
  const shown = firstAsk < 0 ? [] : turns.slice(firstAsk);

  const send = (text: string) => { route(text); setDraft(""); };
  const starters = starterQuestions({ screen: context.screen, label: context.label, card, solo: team.data?.cohort.mode === "solo" });
  const fresh = shown.length === 0 && pending === null;

  return (
    <div className="gx-coach" data-testid="coach.thread">
      {card === undefined ? null : (
        <section className="gx-coach-card" data-testid="coach.card">
          <div className="gx-eyebrow">Concept card</div>
          <h3 className="gx-coach-card-t">{card.title}</h3>
          <p className="gx-coach-card-why" data-testid="coach.card.why">{card.whyItMatters}</p>
          <details className="gx-coach-card-more">
            <summary>Details</summary>
            <p>{card.summary}</p>
            <RovingGroup className="gx-coach-card-tabs" role="tablist" follow={false}>
              {([["explainer", "Explain"], ["mistakes", "Common mistakes"]] as const).map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={open === key} className="gx-chip" onClick={() => setOpen(open === key ? null : key)}>{label}</button>
              ))}
            </RovingGroup>
            {open === null ? null : (
              <div className="gx-coach-card-b">
                {open === "explainer" ? <p>{card.explainer}</p> : <ul>{card.misconceptions.map((m) => <li key={m}>{m}</li>)}</ul>}
              </div>
            )}
          </details>
        </section>
      )}

      <div className="gx-coach-thread" aria-live="polite">
        {fresh ? <Bubble role="coach" testId="coach.welcome" text={WELCOME} /> : null}
        {shown.map((t) => <Bubble key={t.id} role={t.role} text={t.text} />)}
        {pending === null ? null : (
          <>
            <Bubble role="me" text={pending.userText} />
            <Bubble role="coach" testId="coach.reply" error={pending.error !== null} text={pending.error !== null ? ERROR_COPY[pending.error] : pending.reply === "" ? "Thinking…" : pending.reply} />
          </>
        )}
        <div ref={end} className="gx-coach-end" />
      </div>

      {fresh ? (
        <div className="gx-coach-starters" data-testid="coach.starters">
          {starters.map((q) => <button key={q} type="button" className="gx-chip gx-coach-starter" disabled={busy || teamId === null} onClick={() => send(q)}>{q}</button>)}
        </div>
      ) : null}

      <form className="gx-coach-composer" onSubmit={(e) => { e.preventDefault(); if (!busy && !over) send(draft); }}>
        {card === undefined ? null : (
          <div className="gx-coach-hints" aria-label="Hints">
            <GxBadge tone="neutral">Hints</GxBadge>
            {HINT_ASKS.map((q, i) => <button key={q} type="button" className="gx-chip" aria-label={`Hint ${i + 1}`} disabled={busy || teamId === null} onClick={() => send(q)}>{HINT_MARKS[i]}</button>)}
          </div>
        )}
        <div onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (!busy && !over) send(draft); } }}>
          <GxLimitedText id="coach-ask" label="Ask the Coach" labelHidden value={draft} max={INPUT_LIMITS.coachAsk} rows={2} placeholder="Ask about a term, a decision or last year's result" onChange={(text, isOver) => { setDraft(text); setOver(isOver); }} />
        </div>
        <button type="submit" className="gx-btn gx-btn--primary gx-coach-ask" disabled={busy || over || draft.trim() === "" || teamId === null}>Ask</button>
        <p className="gx-small gx-faint" data-testid="coach.privacy">{solo ? "Your coach notes are private. The Coach will not tell you what to choose." : "Your facilitator can read these conversations. The Coach will not tell you what to choose."}</p>
      </form>
    </div>
  );
}
